import { useState, useEffect } from 'react'
import { ethers } from 'ethers'
import { Button } from '../Button'
import StakingModal from '../StakingModal'
import UnstakeModal from '../UnstakeModal'
import StakingHistoryItem from '../StakingHistoryItem'
import Spinner from '../Spinner'
import { formatDate } from '../../utils/formatDate'
import { formatEtherToStr } from '../../utils/ether-utilities'
import networks from '../../networks.json'
import { CHAIN_ID } from '../../constants'
import {
  truncateDecimalDigit,
  truncateHarvestDecimalDigit,
} from '../../utils/truncateDecimalDigit'
import { getFixedStaking, getMetarunToken } from '../../api/contracts'

import {
  StyledCard,
  CardHeader,
  CardHeaderItem,
  CardHeaderItemTitle,
  CardHeaderItemText,
  CardHeaderItemWrapper,
  CardBody,
  CardBalance,
  CardBalanceTitle,
  CardIconLogo,
  CardIconStaking,
  CardHistory,
  CardHistoryText,
  CardHistoryIcon,
  CardHistoryShowButton,
  CardHistoryTextContainer,
  CardHistoryTextWrapper,
  Border,
  CardCharacterRatio,
  CardCharacterIcon,
  CardInfoTitle,
  CardInfo,
  CardQuestion,
  Tooltip,
  CardQuestionWrapper,
} from './StakingCard.styled'

export function StakingCard({
  APY,
  name,
  contractAddress,
  mrunBalance,
  userAddress,
  chainId,
  duration,
  tableMrunPerCharacter,
  earnDynamicAPY,
  tableYieldRate,
  forceUpdate,
  forced,
}) {
  const [yieldRate, setYieldRate] = useState(0)
  const [mrunPerCharacter, setMrunPerCharacter] = useState(0)
  const [earlyUnstakeFee, setEarlyUnstakeFee] = useState(0)
  const [stakingHistory, setStakingHistory] = useState([])
  const [stakesLength, setStakesLength] = useState(0)
  const [totalStaked, setTotalStaked] = useState(0)
  const [allowanceEnough, setAllowanceEnough] = useState(true)
  const [stakeDuration, setStakeDuration] = useState(0)
  const [allowance, setAllowance] = useState(0)

  const [isStakingModalOpen, setStakingModalOpen] = useState(false)
  const [isUnstakeModalOpen, setUnstakeModalOpen] = useState(false)
  const [unstakeIdx, setUnstakeIdx] = useState(0)
  const [unstakeStaked, setUnstakeStaked] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [isHistoryMenuOpen, setIsHistoryMenuOpen] = useState(false)

  const handleOpenStakingModalWindow = () => setStakingModalOpen(true)
  const handleCloseStakingModalWindow = () => setStakingModalOpen(false)

  const handleOpenUnstakeModalWindow = () => setUnstakeModalOpen(true)
  const handleCloseUnstakeModalWindow = () => setUnstakeModalOpen(false)

  function getStakingPicture(name) {
    switch (name) {
      case 'Epic':
        return require('../../images/GoldenDiamond.svg').default
      case 'Legendary':
        return require('../../images/PinkDiamond.svg').default
      default:
        return require('../../images/GreenDiamond.svg').default
    }
  }

  function getCharacterIcon(name) {
    switch (name) {
      case 'Epic':
        return require('../../images/GoldenHelmet.svg').default
      case 'Legendary':
        return require('../../images/PinkHelmet.svg').default
      default:
        return require('../../images/GreenHelmet.svg').default
    }
  }

  function getData() {
    try {
      const FixedStaking = getFixedStaking(contractAddress)
      const MetarunToken = getMetarunToken()

      FixedStaking.stakeDuration().then(res => setStakeDuration(res.toNumber()))
      FixedStaking.earlyUnstakeFee().then(res =>
        setEarlyUnstakeFee(res.toNumber() / 100)
      )
      FixedStaking.yieldRate().then(res => setYieldRate(res.toNumber() / 100))
      FixedStaking.mrunPerCharacter().then(res => setMrunPerCharacter(res))
      FixedStaking.getStakesLength(userAddress).then(res => {
        setStakesLength(res.toNumber())
      })
      MetarunToken.allowance(userAddress, contractAddress).then(res =>
        setAllowance(res.toString())
      )
    } catch (error) {
      console.log(error)
    }
    setIsLoading(false)
  }

  async function getStakes() {
    try {
      const FixedStaking = getFixedStaking(contractAddress)
      const stakes = []
      let totalStaked = ethers.BigNumber.from('0')
      for (let i = 0; i < stakesLength; i++) {
        const stake = await FixedStaking.getStake(userAddress, i)
        stakes.push(stake)
      }

      const formatedStakes = stakes.map((stake, idx) => {
        const {
          staked,
          endTime,
          harvestableYield,
          stakedAmount,
          charactersAmount,
        } = {
          staked: stake.staked,
          endTime: stake.endTime.toString(),
          harvestableYield: truncateHarvestDecimalDigit(
            formatEtherToStr(stake.harvestableYield)
          ),
          stakedAmount: truncateDecimalDigit(
            formatEtherToStr(stake.stakedAmount)
          ),
          charactersAmount: stake.charactersAmount.toNumber(),
        }

        totalStaked = staked ? totalStaked.add(stake.stakedAmount) : totalStaked
        const expired = endTime * 1000 < Date.now()
        return {
          staked,
          stakedAmount,
          harvestable: harvestableYield,
          charactersAmount,
          allowHarvest: !stake.harvestableYield.isZero(),
          expires: expired ? 'Expired' : formatDate(endTime),
          restTimeBeforeExpire: expired ? 0 : endTime - Date.now() / 1000,
        }
      })
      setStakingHistory(formatedStakes)
      setTotalStaked(
        truncateDecimalDigit(ethers.utils.formatEther(totalStaked.toString()))
      )
    } catch (error) {
      console.log(error)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (userAddress && chainId === networks[CHAIN_ID].params.chainId) {
      setIsLoading(true)
      getData()
    } else {
      setStakesLength(0)
    }
  }, [userAddress, chainId, forced])

  useEffect(() => {
    if (stakesLength > 0) {
      setIsLoading(true)
      getStakes()
    } else {
      setStakingHistory([])
    }
  }, [stakesLength, forced])

  return (
    <>
      <StyledCard>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <CardHeader isOpen={isHistoryMenuOpen}>
              <CardHeaderItem>
                <CardIconStaking
                  src={getStakingPicture(name)}
                  width='32'
                  height='32'
                  alt='name'
                ></CardIconStaking>
                <CardHeaderItemTitle> {name}</CardHeaderItemTitle>
              </CardHeaderItem>
              <CardHeaderItemWrapper>
                <CardHeaderItem>
                  <CardHeaderItemText>APY:</CardHeaderItemText> {APY}%
                </CardHeaderItem>
                <CardHeaderItem>{duration} days</CardHeaderItem>
              </CardHeaderItemWrapper>
            </CardHeader>
            <Border />
            {!stakingHistory || stakingHistory.length === 0 ? (
              <>
                <CardHistory isOpen={isHistoryMenuOpen}>
                  <CardCharacterRatio>
                    {tableMrunPerCharacter} MRUN for Character{' '}
                    <CardCharacterIcon
                      src={getCharacterIcon(name)}
                      width='24px'
                      height='24px'
                      alt='character'
                    />
                  </CardCharacterRatio>
                  <CardHistoryTextWrapper>
                    <CardHistoryTextContainer />
                    <CardHistoryText>No stakes yet</CardHistoryText>
                  </CardHistoryTextWrapper>
                </CardHistory>
                {isHistoryMenuOpen ? (
                  <CardHistoryShowButton
                    onClick={() => {
                      setIsHistoryMenuOpen(false)
                    }}
                  >
                    Show less{' '}
                    <CardHistoryIcon
                      src={require('../../images/ArrowUp.svg').default}
                      width='24'
                      height='24'
                      alt='down'
                    ></CardHistoryIcon>
                  </CardHistoryShowButton>
                ) : (
                  <CardHistoryShowButton
                    onClick={() => {
                      setIsHistoryMenuOpen(true)
                    }}
                  >
                    Show more{' '}
                    <CardHistoryIcon
                      src={require('../../images/WhiteArrowDown.svg').default}
                      width='24'
                      height='24'
                      alt='down'
                    ></CardHistoryIcon>
                  </CardHistoryShowButton>
                )}
              </>
            ) : (
              <>
                <CardHistory isOpen={isHistoryMenuOpen}>
                  <CardCharacterRatio>
                    {tableMrunPerCharacter} MRUN for Character{' '}
                    <CardCharacterIcon
                      src={getCharacterIcon(name)}
                      width='24px'
                      height='24px'
                      alt='character'
                    />
                  </CardCharacterRatio>
                  {stakingHistory.map(
                    (
                      {
                        staked,
                        stakedAmount,
                        harvestable,
                        charactersAmount,
                        allowHarvest,
                        restTimeBeforeExpire,
                      },
                      idx
                    ) => {
                      return (
                        <StakingHistoryItem
                          key={idx}
                          name={name}
                          setIsLoading={setIsLoading}
                          forceUpdate={forceUpdate}
                          staked={staked}
                          idx={idx}
                          restTimeBeforeExpire={restTimeBeforeExpire}
                          stakeDuration={stakeDuration}
                          harvestable={harvestable}
                          charactersAmount={charactersAmount}
                          allowHarvest={allowHarvest}
                          stakedAmount={stakedAmount}
                          setUnstakeIdx={setUnstakeIdx}
                          setUnstakeStaked={setUnstakeStaked}
                          handleOpenUnstakeModalWindow={
                            handleOpenUnstakeModalWindow
                          }
                          contractAddress={contractAddress}
                        />
                      )
                    }
                  )}
                </CardHistory>
                {isHistoryMenuOpen ? (
                  <CardHistoryShowButton
                    onClick={() => {
                      setIsHistoryMenuOpen(false)
                    }}
                  >
                    Show less{' '}
                    <CardHistoryIcon
                      src={require('../../images/ArrowUp.svg').default}
                      width='24'
                      height='24'
                      alt='down'
                    ></CardHistoryIcon>
                  </CardHistoryShowButton>
                ) : (
                  <CardHistoryShowButton
                    onClick={() => {
                      setIsHistoryMenuOpen(true)
                    }}
                  >
                    Show more{' '}
                    <CardHistoryIcon
                      src={require('../../images/WhiteArrowDown.svg').default}
                      width='24'
                      height='24'
                      alt='down'
                    ></CardHistoryIcon>
                  </CardHistoryShowButton>
                )}
              </>
            )}
            <CardBody border padding='14px 0 0'>
              <CardInfoTitle>Minimum Stake</CardInfoTitle>
              <CardInfo>1 MRUN</CardInfo>
            </CardBody>
            <CardBody margin='0 0 10px' mobileMargin='0 0 2px'>
              <CardInfoTitle>
                <span>Earn Dynamic APY up to</span>
                <CardQuestionWrapper>
                  <CardQuestion
                    src={require('../../images/Question.svg').default}
                    alt='question'
                    width='20'
                    height='20'
                  />
                  <Tooltip>
                    This is an estimated APY on your investment if you win the
                    top rarity NFT and utilize it efficiently in the Metarun
                    game.
                  </Tooltip>
                </CardQuestionWrapper>
              </CardInfoTitle>
              <CardInfo>{earnDynamicAPY} %</CardInfo>
            </CardBody>
            <Border />
            <CardBody
              margin='14px 0 0'
              border
              mobileMargin='14px 0 0'
              padding='14px 0 0'
            >
              <CardBalance>
                <CardBalanceTitle>My total stake:</CardBalanceTitle>
                <div>
                  {totalStaked}
                  <CardIconLogo>
                    <img
                      src={require('../../images/SmallLogo.svg').default}
                      width='25'
                      height='20'
                      alt='mrn'
                    ></img>
                  </CardIconLogo>
                </div>
              </CardBalance>
              <Button
                variant='contained'
                size='staking'
                onClick={handleOpenStakingModalWindow}
                disabled={
                  !userAddress ||
                  !(chainId === networks[CHAIN_ID].params.chainId)
                }
              >
                Stake
              </Button>
            </CardBody>
          </>
        )}
      </StyledCard>
      <StakingModal
        isOpen={isStakingModalOpen}
        onClose={handleCloseStakingModalWindow}
        mrunBalance={mrunBalance}
        allowance={allowance}
        setAllowanceEnough={setAllowanceEnough}
        userAddress={userAddress}
        contractAddress={contractAddress}
        setAllowance={setAllowance}
        allowanceEnough={allowanceEnough}
        yieldRate={yieldRate}
        mrunPerCharacter={mrunPerCharacter}
        stakeDuration={stakeDuration}
        name={name}
        forceUpdate={forceUpdate}
        setCardIsLoading={setIsLoading}
        earlyUnstakeFee={earlyUnstakeFee}
      />
      <UnstakeModal
        isOpen={isUnstakeModalOpen}
        onClose={handleCloseUnstakeModalWindow}
        staked={unstakeStaked}
        idx={unstakeIdx}
        earlyUnstakeFee={earlyUnstakeFee}
        forceUpdate={forceUpdate}
        setCardIsLoading={setIsLoading}
        contractAddress={contractAddress}
        stakingHistory={stakingHistory}
      />
    </>
  )
}
